<template>
<v-layout column>
    <v-flex xs12 v-if="showOfflineWarning">
        <v-alert type="warning" :value="true">
            <span>Warning: Profiles cannot be added, edited or deleted while offline.</span>
        </v-alert>
    </v-flex>
    <v-flex xs12>
        <v-layout wrap>
            <v-flex xs12>
                <v-alert :value="errorMsg != null" outline type="error">
                    {{ errorMsg }}
                </v-alert>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
                <v-autocomplete
                    v-model="selectedDivisionIDs"
                    :items="availableDivisions"
                    label="Division"
                    multiple
                    clearable
                ></v-autocomplete>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
                <v-autocomplete
                    v-model="selectedVarietyIDs"
                    :items="availableVarieties"
                    label="Variety"
                    multiple
                    clearable
                ></v-autocomplete>                
            </v-flex>
            <v-flex xs12 sm6 pa-1>
                <v-autocomplete
                    v-model="selectedCroppingSystemIDs"
                    :items="availableCroppingSystems"
                    label="Cropping System"
                    multiple
                    clearable
                ></v-autocomplete>                                        
            </v-flex>
            <v-flex xs12 sm6 pa-1>
               <v-autocomplete
                    v-model="selectedPlantAgeIDs"
                    :items="availablePlantAges"
                    label="Plant Age"
                    multiple
                    clearable
                ></v-autocomplete>                                        
            </v-flex>
            <v-flex v-if="cropLocationID == null" xs12 sm6 pa-1>
                <v-checkbox class="active-checkbox" label="Active Only" v-model="activeOnly" hide-details></v-checkbox>
            </v-flex> 
            <v-flex v-if="cropLocationID == null" xs12 sm2 style="padding:5px;">
                <v-btn color="primary" block @click="download()">Download</v-btn>
            </v-flex>
            <v-flex v-if="cropLocationID == null" xs12 sm2 style="padding:5px;">
                <v-btn color="primary" block @click="showFileUpload = true">Upload</v-btn>
            </v-flex>
            <v-flex xs12 sm2 style="padding:5px;">
                <v-btn color="primary" block @click="add()">Add</v-btn>
            </v-flex>                     
        </v-layout>
    </v-flex>
    <v-data-table :items="filteredForecastProfileItems" item-key="BerriesForecastProfileID" :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" must-sort>
        <template v-slot:headers="props">
            <tr>
                <th class="noPadding" v-if="selectedProfiles != null"></th>
                <th @click="changeSort('PlantName')" :class="{ ...paginationClassFor('PlantName'), ...tableColumnPaddingClass }" v-if="cropLocationID == null">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Plant</span>
                </th>
                <th @click="changeSort('DivisionName')" :class="{ ...paginationClassFor('DivisionName'), ...tableColumnPaddingClass }" v-if="cropLocationID == null">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Division</span>
                </th>
                <th @click="changeSort('VarietyName')" :class="{ ...paginationClassFor('VarietyName'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Variety</span>
                </th>
                <th @click="changeSort('CroppyingSystem')" :class="{ ...paginationClassFor('CroppingSystem'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Cropping System</span>
                </th>
                <th @click="changeSort('PlantAge')" :class="{ ...paginationClassFor('PlantAge'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Plant Age</span>
                </th>
                <th @click="changeSort('StartWeek')" :class="{ ...paginationClassFor('StartWeek'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Starting Week</span>
                </th>
                <th v-if="cropLocationID != null" @click="changeSort('StartYear')" :class="{ ...paginationClassFor('StartYear'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Starting Year</span>
                </th>
                <th @click="changeSort('TonnesPerHectare')" :class="{ ...paginationClassFor('TonnesPerHectare'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>Tonnage/Ha</span>
                </th>
                <th v-if="cropLocationID == null" @click="changeSort('IsArchived')" :class="{ ...paginationClassFor('IsArchived'), ...tableColumnPaddingClass }">
                    <v-icon small>arrow_upward</v-icon>
                    <span>IsArchived</span>
                </th>
                <th class="noPadding" v-if="cropLocationID != null">
                    <span>Active</span>
                </th>
                <th :class="tableColumnPaddingClass"></th>
                <th :class="tableColumnPaddingClass"></th>
                <th :class="tableColumnPaddingClass"></th>
            </tr>
        </template>
        <template v-slot:items="props">
            <tr>
                <td class="noPadding" align="center" vertical-align="middle" v-if="selectedProfiles != null">
                    <v-btn flat icon @click.stop="selectProfile(props.item)">
                        <v-icon color="accent" v-if="selectedProfiles.some(x => x.BerriesForecastProfileID == props.item.BerriesForecastProfileID)">insert_chart</v-icon>
                        <v-icon color="rgba(0,0,0,0.54)" v-else>check_box_outline_blank</v-icon>
                    </v-btn>
                </td>
                <td :class="tableColumnPaddingClass" v-if="cropLocationID == null">{{props.item.PlantName}}</td>
                <td :class="tableColumnPaddingClass" v-if="cropLocationID == null">{{props.item.DivisionName}}</td>
                <td :class="tableColumnPaddingClass">{{props.item.VarietyName}}</td>
                <td :class="tableColumnPaddingClass">{{props.item.CroppingSystem}}</td>
                <td :class="tableColumnPaddingClass">{{props.item.PlantAge}}</td>
                <td :class="tableColumnPaddingClass">{{props.item.StartWeek}}</td>
                <td :class="tableColumnPaddingClass" v-if="cropLocationID != null">{{props.item.StartYear}}</td>
                <td :class="tableColumnPaddingClass">{{props.item.TonnesPerHectare}}</td>
                <td class="noPadding" align="center" vertical-align="middle" v-if="cropLocationID != null">
                    <v-btn flat icon @click.stop="markActive(props.item.BerriesForecastProfileID)" :disabled="loading">
                        <v-icon color="accent" v-if="props.item.IsActive">radio_button_checked</v-icon>
                        <v-icon color="rgba(0,0,0,0.54)" v-else>radio_button_unchecked</v-icon>
                    </v-btn>
                </td>
                <td :class="tableColumnPaddingClass" v-if="cropLocationID == null">{{props.item.IsArchived ? "Archived" : "Non Archived"}}</td>
                <td :class="tableColumnPaddingClass">
                    <v-btn color="primary" block @click.stop="edit(props.item.BerriesForecastProfileID, true)" :loading="loading">
                        <span>Edit</span>
                    </v-btn>
                </td>
                <td :class="tableColumnPaddingClass">
                    <v-btn color="info" block @click.stop="toggleArchive(props.item.BerriesForecastProfileID)" :loading="loading">
                        <span>{{ props.item.IsArchived ? "Unarchive" : "Archive" }}</span>
                    </v-btn>
                </td>
                <td :class="tableColumnPaddingClass">
                    <v-btn color="error" block @click.stop="checkConfirmDelete(props.item.BerriesForecastProfileID)" :loading="loading">
                        <span>Delete</span>
                    </v-btn>
                </td>
            </tr>
        </template>
    </v-data-table>
    <v-dialog :value="showEdit" fullscreen hide-overlay transition="dialog-bottom-transition" @keydown.esc="cancel">
        <forecast-profile-add-edit
            ref="forecastProfileAddEdit"
            :financialYearCalendars="financialYearCalendars"
            :forecastProfile="forecastProfile || { BudgetValues: [] }"
            :availableGlobalProfiles="plantTemplateProfiles"
            :loading="loading"
            :error="error"
            :showOfflineWarning="showOfflineWarning"
            :showEdit="showEdit"
            @onCancel="cancel"
            @onSave="saveAddOrEdit">
        </forecast-profile-add-edit>
    </v-dialog>
    <v-dialog :value="profileToDelete != null" max-width="290" persistent @keydown.esc="cancelDelete">
        <v-card>
            <v-toolbar>
                <v-toolbar-title>Confirm Delete</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <span>Are you sure you want to delete this profile?</span>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="cancelDelete">Cancel</v-btn>
                <v-btn color="error" @click="confirmDelete" :loading="loading">Delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-dialog :value="uploadResponse != null" max-width="490" persistent @keydown.esc="uploadResponseComplete">
        <v-card>
            <v-toolbar>
                <v-toolbar-title>Upload Profile Templates</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <v-progress-linear :indeterminate="true" v-if="isUploading" ></v-progress-linear>
                <span>{{ uploadResponseTitle() }}</span>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn v-if="!commit" color="error" @click="uploadResponseCancel">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="uploadResponseComplete" :loading="isUploading">
                    <span v-if="!commit">Confirm</span>
                    <span v-else>OK</span>
                </v-btn>                
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-bottom-sheet full-width v-model="showFileUpload" persistent >
        <div class="FileUpload">
            <v-progress-linear v-if="isUploading" indeterminate></v-progress-linear>
            <FileUpload
                @Close="fileUploadClosed"
                @Save="fileUploadSaved"
                :MaxFileSizeMB="100"
                :MaxFileCount="1"
                :IsUploading="isUploading"
                AllowedFileExtensions=".csv"/>
        </div>
    </v-bottom-sheet>
</v-layout>
</template>

<script>
import ForecastProfileAddEdit from '@/components/forecasting/ForecastProfileAddEdit.vue';
import moment from 'moment';
import UtilService from '@/services/utilService.js';
import * as StringHelpers from "@/helpers/stringHelpers.js";
import BlobHelper from "@/helpers/blobHelper.js";
import FileUpload from "@/components/generic/FileUpload.vue";

export default {
    components: {
        ForecastProfileAddEdit,
        FileUpload
    },
    props: [
        'financialYearCalendars',
        'forecastProfiles', /* contains all crop and global profiles */
        'cropLocationID', /* identifies that this is for a crop */
        'showOfflineWarning',
        'selectedProfiles',
        'crop'
    ],
    data() {
        return {
            pagination: {
                page: 1,
                rowsPerPage: 25,
                descending: false
            },
            rowsPerPageItems: [10, 25, 50, 100],
            forecastProfile: null,
            loading: false,
            error: null,
            errorMsg: null,
            profileToDelete: null,
            showEdit: false,
            selectedDivisionIDs: [],
            selectedVarietyIDs: [],
            selectedCroppingSystemIDs: [],
            selectedPlantAgeIDs: [],
            activeOnly: true,
            showFileUpload: false,
            isUploading: false,
            uploadResponse: null,
            commit: false,
            file: null
        }
    },
    computed: {
        forecastProfileItems() {
            let forecastProfiles = this.forecastProfiles;
            if (this.cropLocationID != null) {
                forecastProfiles = forecastProfiles.filter(x => x.CropLocationID == this.cropLocationID);
            }
            
            return forecastProfiles.map(x => {
                return {
                    BerriesForecastProfileID: x.BerriesForecastProfileID,
                    TemplateBerriesForecastProfileID: x.TemplateBerriesForecastProfileID,
                    VarietyName: `${x.Variety.VarietyName} (${x.Variety.VarietyDescription})`,
                    PlantName: x.Plant.PlantName,
                    DivisionName: x.Division.DivisionName,
                    CroppingSystem: x.CroppingSystem.Description,
                    PlantAge: x.PlantAge.Description,
                    StartWeek: x.StartWeek,
                    TonnesPerHectare: x.TonnesPerHectare,
                    CropLocationID: x.CropLocationID,
                    StartYear: x.StartYear,
                    IsActive: x.IsActive,
                    BerriesCroppingSystemID: x.CroppingSystem.BerriesCroppingSystemID,
                    BerriesPlantAgeID: x.PlantAge.BerriesPlantAgeID,
                    DivisionID: x.Division.DivisionID,
                    VarietyID: x.Variety.VarietyID,
                    IsArchived: x.IsArchived
                };
            });
        },
        filteredForecastProfileItems() {
            return this.forecastProfileItems.filter(p=> {
                if(this.selectedDivisionIDs.length > 0 && this.selectedDivisionIDs.indexOf(p.DivisionID) < 0) {
                    return false;
                } 
                if(this.selectedVarietyIDs.length > 0 && this.selectedVarietyIDs.indexOf(p.VarietyID) < 0) {
                    return false;
                } 
                if(this.selectedCroppingSystemIDs.length > 0 && this.selectedCroppingSystemIDs.indexOf(p.BerriesCroppingSystemID) < 0) {
                    return false;
                }
                if(this.selectedPlantAgeIDs.length > 0 && this.selectedPlantAgeIDs.indexOf(p.BerriesPlantAgeID) < 0) {
                    return false;
                }
                if (this.cropLocationID == null && this.activeOnly && p.IsArchived) {
                    return false;
                }  
                return true;
            });
        },
        plantTemplateProfiles() {
            if (this.cropLocationID == null) {
                return null;
            }

            return this.forecastProfiles.filter(x => this.forecastProfileTemplateApplicableToCurrentCrop(x));
        },
        cropProfiles() {
            if (this.cropLocationID == null) {
                return [];
            }

            return this.forecastProfiles.filter(x => x.CropLocationID == this.cropLocationID);
        },
        defaultPlant() {
            let plants = this.$store.getters["ForecastingProfileStore/getPlants"];

            if (plants.length === 1) {
                return plants[0];
            }

            return null;
        },
        defaultDivision() {
            let divisions = this.$store.getters["ForecastingProfileStore/getDivisions"];

            if (divisions.length === 1) {
                return divisions[0];
            }

            return null;
        },
        tableColumnPaddingClass() {
            return {
                'halfPadding': this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.md,
                'noPadding': this.$vuetify.breakpoint.xs
            };
        },
        availableDivisions(){
            let divisions = this.$store.getters["ForecastingProfileStore/getDivisions"];

            divisions = divisions.map(x => {
                return {
                    text: x.DivisionName,
                    value: x.DivisionID
                }
            });

            /* eslint-disable-next-line */
            return divisions.sort(UtilService.sortAscending(x => x.text));
        },
        availableVarieties(){
            let varietySummaries = this.$store.getters["ForecastingProfileStore/getVarietySummaries"];

            varietySummaries = varietySummaries.map(x => {
                return {
                    text: `${x.VarietyName} (${x.VarietyDescription})`,
                    value: x.VarietyID
                }
            });

            /* eslint-disable-next-line */
            return varietySummaries.sort(UtilService.sortAscending(x => x.text));
        },
        availableCroppingSystems() {
            let croppingSystems = this.$store.getters["ForecastingProfileStore/getCroppingSystems"];

            croppingSystems = croppingSystems.map(x => {
                return {
                    text: x.Description,
                    value: x.BerriesCroppingSystemID
                }
            });

            return croppingSystems.sort(UtilService.sortAscending(x => x.text));
        },
        availablePlantAges() {
            let plantAges = this.$store.getters["ForecastingProfileStore/getPlantAges"];

            plantAges = plantAges.map(x => {
                return {
                    text: x.Description,
                    value: x.BerriesPlantAgeID
                }
            });

            return plantAges.sort(UtilService.sortAscending(x => x.text));
        },
    },
    methods: {
        forecastProfileTemplateApplicableToCurrentCrop(profile) {
            // can only apply global profiles for varieties of this crop
            if (profile.CropLocationID) {
                return false;
            }

            // profile must have a variety that matches one of the varieties for the crop
            // profile must have a cropping system that matches the cropping systemn for the matching variety for the crop
            // profile must have a plant age that matches the plant age for the matching variety for the crop

            let varietyFound = false;
            let croppingSystemAttributeForCropFound = false;
            let plantAgeAttributeForCropFound = false;
            let matchingCroppingSystem = false;
            let matchingPlantAge = false;

            for (const cropVariety of this.crop.CropVarieties) {
                if (cropVariety.ProductVarietyID == profile.Variety.VarietyID) {
                    // we've found a matching variety
                    varietyFound = true;

                    if (cropVariety.Attributes) {
                        for (const attribute of cropVariety.Attributes) {
                            if (StringHelpers.stringCaseInsensitiveIgnoreWhitespaceEquals(attribute.AttributeName, "Cropping System")) {
                                if (attribute.Values) {
                                    croppingSystemAttributeForCropFound = true;

                                    for (const attributeValue of attribute.Values) {
                                        if (profile.CroppingSystem && attributeValue.AttributeValueID == profile.CroppingSystem.BerriesCroppingSystemID) {
                                            matchingCroppingSystem = true;
                                        }
                                    }
                                }
                            }

                            if (StringHelpers.stringCaseInsensitiveIgnoreWhitespaceEquals(attribute.AttributeName, "Plant Age")) {
                                if (attribute.Values) {
                                    plantAgeAttributeForCropFound = true;

                                    for (const attributeValue of attribute.Values) {
                                        if (profile.PlantAge && attributeValue.AttributeValueID == profile.PlantAge.BerriesPlantAgeID) {
                                            matchingPlantAge = true;
                                        }
                                    }
                                }
                            }
                        }
                    }
                    else {
                        // no attributes? not sure if this is reasonable, but if it does happen: make sure we don't restrict visibility of profiles
                        matchingCroppingSystem = true;
                        matchingPlantAge = true;
                    }

                    if (!croppingSystemAttributeForCropFound) {
                        // there was no cropping system attribute for the matched variety
                        // we don't need to filter the profile based on the value, so pretend we matched something
                        matchingCroppingSystem = true;
                    }

                    if (!plantAgeAttributeForCropFound) {
                        // there was no plant age attribute for the matched variety
                        // we don't need to filter the profile based on the value, so pretend we matched something
                        matchingPlantAge = true;
                    }
                }
            }

            if (!varietyFound || !matchingCroppingSystem || !matchingPlantAge) {
                return false;
            }

            return true;
        },
        changeSort(column) {
            if (this.pagination.sortBy == null || this.pagination.sortBy != column) {
                this.pagination.sortBy = column;
                this.pagination.descending = false;
            } else if (!this.pagination.descending) {
                this.pagination.descending = true;
            } else {
                this.pagination.sortBy = null;
            }
        },
        paginationClassFor(column) {
            return {
                'column sortable': true,
                'desc': this.pagination.descending,
                'asc': !this.pagination.descending,
                'active': this.pagination.sortBy === column
            };
        },
        selectProfile(profile) {
            this.$emit("onProfileIDSelected", profile.BerriesForecastProfileID);
        },
        add() {
            // Create new model for editing
            this.forecastProfile = {
                BerriesForecastProfileID: null,
                TemplateBerriesForecastProfileID: null,
                Variety: null,
                Plant: this.defaultPlant,
                Division: this.defaultDivision,
                CroppingSystem: null,
                PlantAge: null,
                StartWeek: null,
                TonnesPerHectare: null,
                CropLocationID: this.cropLocationID,
                StartYear: moment().format('YYYY'),
                IsActive: null,
                BudgetValues: []
            };

            this.showEdit = true;
        },
        edit(id, showEdit) {
            // Create new model for editing, based on the existing model
            let forecastProfile = this.forecastProfiles.find(x => x.BerriesForecastProfileID == id);
            this.forecastProfile = {
                BerriesForecastProfileID: forecastProfile.BerriesForecastProfileID,
                TemplateBerriesForecastProfileID: forecastProfile.TemplateBerriesForecastProfileID,
                Variety: {
                    VarietyID: forecastProfile.Variety.VarietyID,
                    VarietyName: forecastProfile.Variety.VarietyName
                },
                Plant: {
                    PlantID: forecastProfile.Plant.PlantID,
                    PlantName: forecastProfile.Plant.PlantName,
                },
                Division: {
                    DivisionID: forecastProfile.Division.DivisionID,
                    DivisionName: forecastProfile.Division.DivisionName
                },
                CroppingSystem: {
                    BerriesCroppingSystemID: forecastProfile.CroppingSystem.BerriesCroppingSystemID,
                    Description: forecastProfile.CroppingSystem.Description
                },
                PlantAge: {
                    BerriesPlantAgeID: forecastProfile.PlantAge.BerriesPlantAgeID,
                    Description: forecastProfile.PlantAge.Description
                },
                StartWeek: forecastProfile.StartWeek,
                TonnesPerHectare: forecastProfile.TonnesPerHectare,
                CropLocationID: forecastProfile.CropLocationID,
                StartYear: forecastProfile.StartYear,
                IsActive: forecastProfile.IsActive,
                BudgetValues: forecastProfile.BudgetValues.map(x => {
                    return {
                        BerriesForecastProfileID: x.BerriesForecastProfileID,
                        WeekNumber: x.BerriesForecastProfileWeekNumber,
                        BudgetValue: x.BudgetValue
                    };
                })
            };

            // we are displaying the add edit dialog => tell the dialog that it is being displayed, so that it can do anything it needs to load data etc
            if (!this.showEdit && showEdit) {
                // we need the nextTick, because without it the forecastProfile prop passed to forecastProfileAddEdit might not have updated when the init() method runs
                this.$nextTick(() => {
                    this.$refs.forecastProfileAddEdit.init();
                });
            }

            this.showEdit = showEdit;
        },
        async markActive(id) {
            this.edit(id, false);

            if (!this.forecastProfile.IsActive) {
                this.forecastProfile.IsActive = true;
                let otherActiveProfiles = this.cropProfiles.filter(c => c.BerriesForecastProfileID != id && c.Variety.VarietyID == this.forecastProfile.Variety.VarietyID && c.IsActive);

                await this.saveAddOrEdit();

                otherActiveProfiles.forEach(async (profile) => {
                    this.edit(profile.BerriesForecastProfileID, false);
                    this.forecastProfile.IsActive = false;
                    await this.saveAddOrEdit();
                });
            } else {
                this.forecastProfile = null;
            }
        },
        checkConfirmDelete(id) {
            this.profileToDelete = id;
        },
        cancelDelete() {
            this.finishConfirmDelete();
        },
        async confirmDelete() {
            await this.deleteItem(this.profileToDelete);

            this.finishConfirmDelete();
        },
        finishConfirmDelete() {
            this.profileToDelete = null;
        },
        async deleteItem(id) {
            this.loading = true;

            // Check if we are deleting the last active record, need to find another one to mark active
            this.edit(id, false);
            if (this.forecastProfile.IsActive) {
                var match = this.cropProfiles.find(c => c.Variety.VarietyID == this.forecastProfile.Variety.VarietyID && !c.IsActive);
                if (match) {
                    this.edit(match.BerriesForecastProfileID, false);
                    this.forecastProfile.IsActive = true;
                    await this.saveAddOrEdit();
                }
            }

            await this.$store.dispatch("ForecastingProfileStore/updateForecastProfile", {
                BerriesForecastProfileID: id,
                ShouldDelete: true
            });

            this.loading = false;
        },
        async toggleArchive(id) {
            this.loading = true;

            await this.$store.dispatch("ForecastingProfileStore/toggleArchiveForecastProfile", {
                BerriesForecastProfileID: id
            });

            this.loading = false;
        },
        cancel() {
            this.forecastProfile = null;
            this.error = null;
            this.showEdit = false;
        },
        async saveAddOrEdit(options) {
            let optionsToSave = {
                updateTemplatedProfileIDs: (options || {}).updateTemplatedProfileIDs
            };

            // There is a multi-select option in the Profile Template dialog for Division when adding a new template. 
            // Create separate records when multiple divisions are created on new profile template
            if (this.forecastProfile.BerriesForecastProfileID == null) {
                await this.saveAdd(optionsToSave, options.divisionIDList);
            } else {
                await this.saveEdit(optionsToSave);
            }
        },
        async saveAdd(options, divisionIDList) {
            this.loading = true;

            if(divisionIDList.length === 0){
                await this.saveEdit(options)
            }
            for (let divisionID of divisionIDList) {
                // Check for a duplicate
                if (this.forecastProfiles.some(p =>
                    p.BerriesForecastProfileID != this.forecastProfile.BerriesForecastProfileID &&
                    p.Plant.PlantID == this.forecastProfile.Plant.PlantID &&
                    p.Division.DivisionID == divisionID &&
                    p.Variety.VarietyID == this.forecastProfile.Variety.VarietyID &&
                    p.CroppingSystem.BerriesCroppingSystemID == this.forecastProfile.CroppingSystem.BerriesCroppingSystemID &&
                    p.PlantAge.BerriesPlantAgeID == this.forecastProfile.PlantAge.BerriesPlantAgeID &&
                    p.StartWeek == this.forecastProfile.StartWeek &&
                    p.StartYear == this.forecastProfile.StartYear)) {

                    this.error = `A profile already exists in ${this.forecastProfile.Division.DivisionName} for ${this.forecastProfile.Variety.VarietyName} with plant age: ${this.forecastProfile.PlantAge.Description}, cropping system: ${this.forecastProfile.CroppingSystem.Description}, starting in week ${this.forecastProfile.StartWeek}${this.forecastProfile.StartYear != null ? ' and year ' + this.forecastProfile.StartYear : ''}`;
                    this.loading = false;

                    return;
                }

                // Do we need to mark this record as active
                if (this.cropLocationID != null) {
                    let matches = this.cropProfiles.some(c => c.Variety.VarietyID == this.forecastProfile.Variety.VarietyID && c.BerriesForecastProfileID != this.forecastProfile.BerriesForecastProfileID && c.IsActive);

                    if (!matches) {
                        this.forecastProfile.IsActive = true;
                    }
                }

                this.error = null;
                try {
                    await this.$store.dispatch("ForecastingProfileStore/updateForecastProfile", {
                        BerriesForecastProfileID: this.forecastProfile.BerriesForecastProfileID,
                        VarietyID: this.forecastProfile.Variety.VarietyID,
                        PlantID: this.forecastProfile.Plant.PlantID,
                        DivisionID: divisionID,
                        BerriesCroppingSystemID: this.forecastProfile.CroppingSystem.BerriesCroppingSystemID,
                        BerriesPlantAgeID: this.forecastProfile.PlantAge.BerriesPlantAgeID,
                        StartWeek: this.forecastProfile.StartWeek,
                        TonnesPerHectare: this.forecastProfile.TonnesPerHectare,
                        CropLocationID: this.forecastProfile.CropLocationID,
                        StartYear: this.forecastProfile.StartYear,
                        IsActive: this.forecastProfile.IsActive,
                        TemplateBerriesForecastProfileID: this.forecastProfile.TemplateBerriesForecastProfileID,
                        UpdateTemplatedBerriesForecastProfileIDs: (options || {}).updateTemplatedProfileIDs,
                        BudgetValues: this.forecastProfile.BudgetValues
                            .filter(x => x.BudgetValue != null && x.BudgetValue !== "")
                            .map(x => {
                                return {
                                    BerriesForecastProfileID: x.BerriesForecastProfileID,
                                    BerriesForecastProfileWeekNumber: x.WeekNumber,
                                    BudgetValue: x.BudgetValue
                                };
                            })
                    });

                } catch (err) {
                    if (err != null && err.response != null && err.response.data != null && err.response.data.Error != null) {
                        this.error = err.response.data.Error;
                    } else {
                        this.error = "Something went wrong.";
                    }
                }
            }
            this.showEdit = false;
            this.forecastProfile = null;
            this.loading = false;
        },
        async saveEdit(options) {
            this.loading = true;

            // Check for a duplicate
            if (this.forecastProfiles.some(p =>
                    p.BerriesForecastProfileID != this.forecastProfile.BerriesForecastProfileID &&
                    p.Plant.PlantID == this.forecastProfile.Plant.PlantID &&
                    p.Division.DivisionID == this.forecastProfile.Division.DivisionID &&
                    p.Variety.VarietyID == this.forecastProfile.Variety.VarietyID &&
                    p.CroppingSystem.BerriesCroppingSystemID == this.forecastProfile.CroppingSystem.BerriesCroppingSystemID &&
                    p.PlantAge.BerriesPlantAgeID == this.forecastProfile.PlantAge.BerriesPlantAgeID &&
                    p.StartWeek == this.forecastProfile.StartWeek &&
                    p.StartYear == this.forecastProfile.StartYear)) {

                this.error = `A profile already exists in ${this.forecastProfile.Division.DivisionName} for ${this.forecastProfile.Variety.VarietyName} with plant age: ${this.forecastProfile.PlantAge.Description}, cropping system: ${this.forecastProfile.CroppingSystem.Description}, starting in week ${this.forecastProfile.StartWeek}${this.forecastProfile.StartYear != null ? ' and year ' + this.forecastProfile.StartYear : ''}`;
                this.loading = false;

                return;
            }

            // Do we need to mark this record as active
            if(this.cropLocationID != null) {
                let matches = this.cropProfiles.some(c => c.Variety.VarietyID == this.forecastProfile.Variety.VarietyID && c.BerriesForecastProfileID != this.forecastProfile.BerriesForecastProfileID && c.IsActive);

                if (!matches) {
                    this.forecastProfile.IsActive = true;
                }
            }

            this.error = null;
            try {
                await this.$store.dispatch("ForecastingProfileStore/updateForecastProfile", {
                    BerriesForecastProfileID: this.forecastProfile.BerriesForecastProfileID,
                    VarietyID: this.forecastProfile.Variety.VarietyID,
                    PlantID: this.forecastProfile.Plant.PlantID,
                    DivisionID: this.forecastProfile.Division.DivisionID,
                    BerriesCroppingSystemID: this.forecastProfile.CroppingSystem.BerriesCroppingSystemID,
                    BerriesPlantAgeID: this.forecastProfile.PlantAge.BerriesPlantAgeID,
                    StartWeek: this.forecastProfile.StartWeek,
                    TonnesPerHectare: this.forecastProfile.TonnesPerHectare,
                    CropLocationID: this.forecastProfile.CropLocationID,
                    StartYear: this.forecastProfile.StartYear,
                    IsActive: this.forecastProfile.IsActive,
                    TemplateBerriesForecastProfileID: this.forecastProfile.TemplateBerriesForecastProfileID,
                    UpdateTemplatedBerriesForecastProfileIDs: (options || {}).updateTemplatedProfileIDs,
                    BudgetValues: this.forecastProfile.BudgetValues
                        .filter(x => x.BudgetValue != null && x.BudgetValue !== "")
                        .map(x => {
                            return {
                                BerriesForecastProfileID: x.BerriesForecastProfileID,
                                BerriesForecastProfileWeekNumber: x.WeekNumber,
                                BudgetValue: x.BudgetValue
                            };
                        })
                });
                this.forecastProfile = null;
                this.showEdit = false;
            } catch (err) {
                if (err != null && err.response != null && err.response.data != null && err.response.data.Error != null) {
                    this.error = err.response.data.Error;
                } else {
                    this.error = "Something went wrong.";
                }
            }

            this.loading = false;
        },
        clearFilters() {
            this.selectedDivisionIDs = [];
            this.selectedVarietyIDs = [];
            this.selectedCroppingSystemIDs = [];
            this.selectedPlantAgeIDs = [];
        },
        async download() { 
            const plant = this.defaultPlant;
            if (plant != null) {
                let items = this.filteredForecastProfileItems;
                let profileTemplateIDs = items.map(o => o.BerriesForecastProfileID);
                const blob = await this.$store.dispatch("ForecastingProfileStore/downloadPlantProfileTemplateData", {
                    ProfileTemplateIDs: profileTemplateIDs
                });
                BlobHelper.downloadBlob(blob, plant.PlantName + '_ProfileTemplates.csv');
            }
        },
        async fileUploadSaved(files, commit = false) {
            try {
                let response = {};
                this.isUploading = true;
                this.commit = commit;

                let file = files[0];
                this.file = file;
                response = await this.$store.dispatch("ForecastingProfileStore/uploadProfileTemplates",  { file, commit });

                if (!response.Success) {
                    this.errorMsg = response.ErrorMessages.join(', ');
                } else {
                    this.uploadResponse = response;
                }
                this.isUploading = false;
            } catch (err) {
                this.error = err;
                return;
            } finally {
                this.showFileUpload = false;
            }
        },
        async fileUploadClosed() {
            this.showFileUpload = false;
        },
        async uploadResponseComplete() {
            if (!this.commit) {
                this.uploadResponse = "Processing";
                await this.fileUploadSaved([ this.file ], true);
            } else {
                this.uploadResponse = null;
                this.file = null;
                this.commit = false;
                this.$emit("ReloadData");
            }
        },
        uploadResponseCancel() {
            this.uploadResponse = null;
            this.file = null;
            this.commit = false;
        },
        uploadResponseTitle() {
            if (this.uploadResponse == null || this.uploadResponse == "Processing") {
                return "Processing...";
            }

            if (this.commit) {
                return this.uploadResponse.AddedTemplateCount + " template(s) added and " + this.uploadResponse.UpdatedTemplateCount + " template(s) updated";
            } else {
                return `Click confirm to add ${this.uploadResponse.AddedTemplateCount} template(s) and overwrite ${this.uploadResponse.UpdatedTemplateCount} template(s). Template(s) with the following ID's will be overwitten: ${ this.uploadResponse.UpdatedTemplates.join(', ') }`;
            }
        }
    },
    created() {

    }
}
</script>

<style scoped>
.noPadding {
    padding: 0px !important;
}

.halfPadding {
    padding: 0px 12px !important;
}

.FileUpload {
    background-color: white;
    padding: 15px;
    min-height: 300px;
}
</style>
